import { useState } from "react";
import { Box, Flex } from "rebass/styled-components";
import { useLocation } from "react-router-dom";
import styled from "styled-components";

import { InfoSection } from "../InfoSection";
import { Tabs } from "MVVM/Components/Tabs";
import { Divider } from "MVVM/Components/Divider";
import { ShareSection } from "../ShareSection";
import { CampaignContentCallout } from "MVVM/Views/Competitions/Components/CampaignContentCallout";
import { EClipPageSource, IClip } from "MVVM/@types";
import { ClipComments } from "MVVM/Components/Comments/Clip";
import { Feed } from "../Feed";
import { CreatorCards } from "../CreatorCards";
import { FollowList } from "../FollowList";
import { getQueryParam } from "MVVM/Utilities/QueryParam";

interface ISidebarProps {
  clip: IClip;
  referrer: string | undefined;
  source: EClipPageSource;
}

const Sidebar = ({ clip, source }: ISidebarProps) => {
  const tabContent = [];
  const clipInCampaign = !!clip?.campaign?.slug;
  const [commentCount, setCommentCount] = useState<number | null>(null);
  const commentsDisabled = clip.user?.commentsDisabled;
  const location = useLocation<{ context: { metadata: { tab: string } } }>();
  const selectedTab =
    getQueryParam("tab") || location?.state?.context?.metadata?.tab;

  const tabs = {
    comments: {
      component: (
        <ClipComments
          clipId={clip.id}
          setCommentCount={setCommentCount}
          disabled={commentsDisabled}
        />
      ),
      name: "comments",
      title: `Comments ${
        !commentsDisabled && Number.isInteger(commentCount)
          ? `(${commentCount})`
          : ""
      }`,
      type: "Comments",
    },
    creatorCards: {
      component: <CreatorCards effects={clip.effects} />,
      name: "creator-cards",
      title: "Creator Cards",
      type: "Creator Cards",
    },
    forYouFeed: {
      component: <Feed />,
      name: "recommended",
      title: "Recommended",
      type: "Recommended",
    },
    followList: {
      component: (
        <FollowList
          players={clip?.moment?.match?.players}
          matchId={clip?.moment?.match?.id}
        />
      ),
      name: "in-this-match",
      title: "In This Match",
      type: "In This Match",
    },
  };

  switch (source) {
    case EClipPageSource.DIRECT:
      tabContent.push(tabs.comments, tabs.forYouFeed, tabs.followList);
      break;
    case EClipPageSource.MODAL:
      tabContent.push(tabs.comments, tabs.followList, tabs.creatorCards);
      break;
    default:
      break;
  }

  const defaultTab = tabContent.findIndex((tab) => tab.name === selectedTab);

  return (
    <SidebarContainer>
      <InfoSection clip={clip} />
      {clipInCampaign && (
        <CampaignContentCallout
          compact={true}
          marketingContent={clip?.campaign?.marketing}
          slug={clip?.campaign?.slug}
          endsAt={clip?.campaign?.endsAt}
          primaryColor={clip?.campaign?.primaryColor}
          secondaryColor={clip?.campaign?.secondaryColor}
        />
      )}
      <Divider />
      <ShareSection clip={clip} />
      <Divider />
      <ColumnContainer>
        <Tabs tabs={tabContent} active={defaultTab === -1 ? 0 : defaultTab} />
      </ColumnContainer>
    </SidebarContainer>
  );
};

const SidebarContainer = styled(Flex)`
  background: ${({ theme }) => theme.colors.darkerNed};
  flex-direction: column;
  scrollbar-width: none;
  width: 441px;
  z-index: 2;

  @media (max-width: ${(props) => props.theme.breaks.theater}) {
    flex-grow: 1;
    overflow: scroll;
    width: 100%;
  }
`;

const ColumnContainer = styled(Box)`
  height: 100%;
  overflow-y: scroll;
  scrollbar-width: none;
  width: 100%;

  ::-webkit-scrollbar {
    display: none;
  }
`;

export { Sidebar };
